import { APP_TYPES } from "@lhd/components/CloneApp/constants";
import Loading from "@lhd/components/Loading";
import { ROUTES } from "@lhd/constants";
import { useFetchRecordV2, useGetAuth } from "@lhd/hooks";
import { AppRecordV2 } from "@lhd/types";
import { toString } from "lodash";
import { createContext, FC, ReactNode, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";

interface Props {
  children: ReactNode;
  appType: typeof APP_TYPES[keyof typeof APP_TYPES];
}

const STATUS_ENUM = {
  LOADING: "loading",
  ERROR: "error",
  SUCCESS: "success",
};

export const CompanyPolicyContext = createContext<{
  data: AppRecordV2 | null;
  loading: boolean;
}>({
  data: null,
  loading: true,
});

const CompanyPolicyProtectedRoute: FC<Props> = ({ children, appType }) => {
  const { appId, id } = useParams();
  const params = new URLSearchParams(location.search);
  const pageMode = params.get("pageMode");
  const { isUserResigned } = useGetAuth();

  const {
    data: recordDataRaw,
    status,
    isError,
  } = useFetchRecordV2({
    appType: appType,
    appId: toString(appId),
    recordId: toString(id),
  });

  const permissionState = useMemo(() => {
    if (status === STATUS_ENUM.LOADING) {
      return {
        isLoading: true,
        data: null,
        shouldRedirect: false,
      };
    }

    if (status === STATUS_ENUM.ERROR || isError) {
      return {
        isLoading: false,
        data: null,
        shouldRedirect: true,
      };
    }

    if (status === STATUS_ENUM.SUCCESS) {
      if (!recordDataRaw) {
        return {
          isLoading: false,
          data: null,
          shouldRedirect: true,
        };
      }
      const { policy_permissions } = recordDataRaw?.data as AppRecordV2;
      const { record_read, record_edit } = policy_permissions ?? {};
      const shouldRedirect =
        (pageMode === "view" && !record_read) ||
        (pageMode === "edit" && !record_edit);
      return {
        isLoading: false,
        data: recordDataRaw?.data as AppRecordV2,
        shouldRedirect,
      };
    }

    return {
      isLoading: true,
      data: null,
      shouldRedirect: false,
    };
  }, [status, recordDataRaw, isError, pageMode]);

  if (permissionState.isLoading) {
    return <Loading />;
  }

  if (permissionState.shouldRedirect || isUserResigned) {
    return <Navigate to={`/${ROUTES.NOT_FOUND_PAGE}`} replace />;
  }

  return (
    <CompanyPolicyContext.Provider
      value={{
        data: permissionState.data as AppRecordV2,
        loading: permissionState.isLoading,
      }}
    >
      {children}
    </CompanyPolicyContext.Provider>
  );
};

export default CompanyPolicyProtectedRoute;
